
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { validateCandidateDataIntegrity } from "../utils/candidateUtils";
import { Candidate } from "../candidate-types";

export const useDataIntegrity = (
  projectId: string | null,
  candidatesPerRole: { [roleId: string]: Candidate[]; }
) => {
  
  useEffect(() => {
    const performIntegrityCheck = async () => {
      if (!projectId) return;

      try {
        console.log('[DataIntegrity] Starting data integrity check...');

        // Check for orphaned candidates in database
        const { data: dbCandidates, error } = await supabase
          .from('candidates')
          .select('id, candidate_name, role_title, candidate_id, match_percentage, created_at')
          .eq('project_id', projectId);

        if (error) {
          console.error('[DataIntegrity] Error fetching candidates for integrity check:', error);
          return;
        }

        if (dbCandidates && dbCandidates.length > 0) {
          // Group by role and check for issues
          const candidatesByRole = dbCandidates.reduce((acc, candidate) => {
            const role = candidate.role_title;
            if (!acc[role]) acc[role] = [];
            acc[role].push(candidate);
            return acc;
          }, {} as Record<string, typeof dbCandidates>);

          let totalIssues = 0;
          Object.keys(candidatesByRole).forEach(role => {
            const roleCandidates = candidatesByRole[role];
            
            // Check for duplicate candidate_ids within the same role
            const candidateIds = roleCandidates.map(c => c.candidate_id).filter(id => id !== null);
            const uniqueIds = new Set(candidateIds);
            
            if (candidateIds.length !== uniqueIds.size) {
              console.warn(`[DataIntegrity] Duplicate candidate_id values found in role ${role}:`, roleCandidates.map(c => ({
                id: c.id,
                name: c.candidate_name,
                candidate_id: c.candidate_id,
                created_at: c.created_at
              })));
              totalIssues++;
            }

            // Check for candidates with the same name in the same role
            const candidateNames = roleCandidates.map(c => c.candidate_name);
            const uniqueNames = new Set(candidateNames);
            if (candidateNames.length !== uniqueNames.size) {
              console.warn(`[DataIntegrity] Duplicate candidate names found in role ${role}:`, candidateNames);
              totalIssues++;
            }

            // Check for old vs new candidates (created more than a day apart)
            const sortedByDate = roleCandidates.sort((a, b) => 
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
            
            if (sortedByDate.length > 1) {
              const oldest = new Date(sortedByDate[0].created_at);
              const newest = new Date(sortedByDate[sortedByDate.length - 1].created_at);
              const daysDiff = (newest.getTime() - oldest.getTime()) / (1000 * 60 * 60 * 24);
              
              if (daysDiff > 1) {
                console.warn(`[DataIntegrity] Candidates with significant age difference found in role ${role}:`, {
                  oldest: { name: sortedByDate[0].candidate_name, created_at: sortedByDate[0].created_at },
                  newest: { name: sortedByDate[sortedByDate.length - 1].candidate_name, created_at: sortedByDate[sortedByDate.length - 1].created_at },
                  daysDifference: daysDiff.toFixed(1)
                });
                totalIssues++;
              }
            }
          });

          if (totalIssues > 0) {
            console.warn(`[DataIntegrity] Found ${totalIssues} data integrity issues. Consider clearing and regenerating candidates.`);
          } else {
            console.log('[DataIntegrity] Database integrity check passed');
          }
        }

        // Validate in-memory candidate data
        Object.keys(candidatesPerRole).forEach(roleId => {
          const candidates = candidatesPerRole[roleId];
          if (candidates && candidates.length > 0) {
            validateCandidateDataIntegrity(candidates, `role ${roleId}`);
          }
        });

        console.log('[DataIntegrity] Integrity check completed');
      } catch (error) {
        console.error('[DataIntegrity] Error during integrity check:', error);
      }
    };

    performIntegrityCheck();
  }, [projectId, candidatesPerRole]);

  return null; // This hook doesn't return any UI, just performs checks
};
